import { Modal } from 'components/modal/Modal.tsx';
import { ModalHeader } from 'components/modal/ModalHeader.tsx';
import { localize, populate } from 'i18n/localize.ts';
import { ModalSubtitle } from 'components/modal/ModalSubtitle.tsx';
import { WatchdogFilterBox } from 'components/modal/watchdog/WatchdogFilterBox.tsx';
import { WatchdogForm } from 'components/modal/watchdog/WatchdogForm.tsx';
import { useCallback, useEffect } from 'preact/hooks';
import { useSignal } from '@preact/signals';
import {
	createWatchdog,
	getWatchdogs,
	removeWatchdog,
	updateWatchdog,
	WATCHDOG_MAX_AMOUNT,
} from 'service/watchdog/resource';
import { toastMessages } from 'components/toastMessage/signals.ts';
import {
	modalizeWatchdogFilter,
	normalizeWatchdogFilter,
	type RequestCreateWatchdogFilter,
	type ResponseWatchdogDataFilter,
	type WatchdogNotificationInterval,
} from 'service/watchdog/model.ts';
import isEqual from 'lodash/isEqual';
import { LOCALITY_CELA_CR_AS_SUGGESTION, PRICE } from 'utils/constants';
import type { URLFilterParams } from 'utils/url.ts';
import { localityToFilterLocality, localitySuggestionToFilterLocality } from 'signals/filters';
import { GET_GPS_LOCATION_ID } from 'components/filters/location/SuggestionsUtils';
import { getCurrentPositionLocality } from 'lib/geo/geo';
import { sendDeleteSavedSearchSearchAnalytics, sendSavedSearchAnalytics } from 'lib/dot/savedSearches/utils.ts';
import { resolveLocality } from 'service/locality/resource';
import type { LocalityData } from 'service/locality/model.ts';
import { ApiError, type ApiErrorsResponse } from 'utils/apiError';

export type ModalWatchdogDataFilter = Omit<ResponseWatchdogDataFilter, 'locality'> & {
	locality?: URLFilterParams['locality'];
};

export type ModalWatchdogData = {
	filter: ModalWatchdogDataFilter;
	notification_interval: WatchdogNotificationInterval;
	use_seznam_bar: boolean;
};

export type Props = {
	initialData: ModalWatchdogData;
	editId?: number | null | undefined;
	onWatchdogDelete?: () => void;
	onWatchdogUpdated?: (watchdogId: number) => Promise<void> | void;
	onModalClose: () => void;
};

const $t = localize();

function modalWatchdogFilterToRequestWatchdogFilter(filter: ModalWatchdogDataFilter): RequestCreateWatchdogFilter {
	const { locality, radius_precision, ...rest } = filter;

	return {
		...rest,
		...(locality &&
			locality.id !== 0 && {
				locality: {
					entity_id: locality.id,
					entity_type: locality.type,
				},
				radius_precision,
			}),
	};
}

function isFilterEqual(filterA: ModalWatchdogDataFilter, filterB: ModalWatchdogDataFilter) {
	// Separate locality, radius_precision and category_id for special comparison, rest is compared as it is
	const { locality: localityA, radius_precision: radiusPrecisionA, category_id: categoryIdA, ...restA } = filterA;
	const { locality: localityB, radius_precision: radiusPrecisionB, category_id: categoryIdB, ...restB } = filterB;

	// EntityId = 0 means whole Czechia which is at times equal to locality being undefined
	const localityAId = localityA?.id || undefined;
	const localityBId = localityB?.id || undefined;
	const hasLocality = !!(localityAId || localityBId);

	return (
		localityAId === localityBId &&
		(hasLocality ? localityA?.type === localityB?.type && radiusPrecisionA === radiusPrecisionB : true) &&
		// CategoryId = 0 means all categories which is at times equal to category being undefined
		(categoryIdA || undefined) === (categoryIdB || undefined) &&
		isEqual({ ...restA }, { ...restB })
	);
}

export function WatchdogModal({
	onModalClose,
	initialData,
	onWatchdogDelete,
	editId: _editId,
	onWatchdogUpdated,
}: Props) {
	const editId = useSignal<number | undefined | null>(_editId);
	const forceDisabled = useSignal(false);
	const loadingGps = useSignal(false);
	const watchdogData = useSignal<ModalWatchdogData>(initialData);
	const editedWatchdogLocality = useSignal<LocalityData | undefined>(undefined);
	const { notification_interval, use_seznam_bar } = watchdogData.value;

	const onSubmit = async () => {
		if (forceDisabled.peek()) return;
		forceDisabled.value = true;
		const _editId = editId.peek();
		const isEditMode = _editId !== undefined && _editId !== null;
		const successToast = isEditMode ? $t.watchdog.toastUpdateSuccess : $t.watchdog.toastCreateSuccess;
		const errorToast = isEditMode ? $t.watchdog.toastUpdateError : $t.watchdog.toastCreateError;

		const watchdogDataValue = watchdogData.peek();

		if (
			isEditMode &&
			isFilterEqual(watchdogDataValue.filter, initialData.filter) &&
			watchdogDataValue.notification_interval === initialData.notification_interval
		) {
			onModalClose();
			forceDisabled.value = false;
			return;
		}

		try {
			const newFilter = modalWatchdogFilterToRequestWatchdogFilter({ ...watchdogData.value.filter });
			const newWatchdog = {
				...newFilter,
				notification_interval: notification_interval,
			};

			const operation = isEditMode ? updateWatchdog(_editId, newWatchdog) : createWatchdog(newWatchdog);
			await operation;
			if (isEditMode && onWatchdogUpdated) {
				await Promise.resolve(onWatchdogUpdated(_editId));
			}

			toastMessages.addMessage(successToast, 'success');
			const resolvedLocality = newFilter.locality?.entity_id
				? await resolveLocality({
						entity_id: newFilter.locality?.entity_id,
						entity_type: newFilter.locality?.entity_type,
					})
				: undefined;
			sendSavedSearchAnalytics(
				{ ...newFilter, ...(resolvedLocality ? { locality: resolvedLocality } : { locality: undefined }) },
				notification_interval,
				isEditMode ? 'edit' : 'save',
			);
		} catch (error) {
			if (error instanceof ApiError && error.cause.status === 403) {
				const errorBody: ApiErrorsResponse = await error.cause.json();
				const watchdogLimitError = errorBody.errors?.find(
					(errorResponse) => errorResponse.error_code === 'watchdog_limit_reached',
				);
				if (watchdogLimitError) {
					const limit = watchdogLimitError.error_data?.limit ?? WATCHDOG_MAX_AMOUNT;
					toastMessages.addMessage(populate($t.watchdog.toastLimitError, { LIMIT: limit }), 'error');
					return;
				}
			}
			console.error(error);
			toastMessages.addMessage(errorToast, 'error');
		} finally {
			onModalClose();
			forceDisabled.value = false;
		}
	};
	const _onDelete = useCallback(async () => {
		const watchdogId = editId.peek();
		if (forceDisabled.peek() || typeof watchdogId !== 'number') return;
		forceDisabled.value = true;

		try {
			await removeWatchdog(watchdogId);
			toastMessages.addMessage($t.watchdog.toastDeleteSuccess, 'success');
			onWatchdogDelete?.();
			sendDeleteSavedSearchSearchAnalytics(
				{
					// @ts-expect-error if the locality type is wrong inside the `normalizeWatchdogFilter`, then it gets fixed via the `editedWatchdogLocality`. This might happen due to differences in filters data structure inside URL, local/remote watchdog and locality input when editing...
					...normalizeWatchdogFilter(watchdogData.peek().filter),
					...(editedWatchdogLocality.peek() && { locality: editedWatchdogLocality.peek() }),
				},
				notification_interval,
			);
		} catch (e) {
			console.error(e);
			toastMessages.addMessage($t.watchdog.toastDeleteError, 'error');
		} finally {
			forceDisabled.value = false;
			onModalClose();
		}
	}, []);

	useEffect(() => {
		if (editId.value !== undefined) return;
		const compareFilter = watchdogData.value.filter ?? {};

		getWatchdogs().then((watchdogs) => {
			const watchdogToEdit = watchdogs.find((watchdog) => {
				return isFilterEqual(modalizeWatchdogFilter(watchdog.filter), compareFilter);
			});

			if (watchdogToEdit) {
				editedWatchdogLocality.value = watchdogToEdit.filter.locality;
				editId.value = watchdogToEdit?.id ?? null;
				watchdogData.value = watchdogToEdit
					? {
							...watchdogData.value,
							notification_interval: watchdogToEdit.notificationInterval,
						}
					: watchdogData.value;
			} else {
				editId.value = null;
			}
		});
	}, []);

	return editId.value !== undefined ? (
		<Modal
			onClickAway={onModalClose}
			onClose={onModalClose}
			className="bg-neutral-white w-[512px] max-w-full p-6 overflow-auto max-h-[90vh]"
		>
			<ModalHeader header={editId.value ? $t.watchdog.edit : $t.watchdog.create} onClose={onModalClose} />
			{!editId.value && <ModalSubtitle subtitle={$t.watchdog.subtitle} />}
			<WatchdogFilterBox
				filter={watchdogData.value.filter}
				forceDisabled={forceDisabled.value}
				showDeleteButton={!!editId.value}
				onDelete={_onDelete}
				onAgreementChanged={(value) =>
					(watchdogData.value = {
						...watchdogData.value,
						filter: { ...watchdogData.value.filter, hide_price_by_agreement: !value },
					})
				}
				onPriceChanged={(values) => {
					watchdogData.value = {
						...watchdogData.value,
						filter: {
							...watchdogData.value.filter,
							price_from: values.from,
							price_to: values.to,
							price_for_free: values.type === PRICE.FREE,
						},
					};
				}}
				onPriceSubmit={(values) => {
					watchdogData.value = {
						...watchdogData.value,
						filter: {
							...watchdogData.value.filter,
							price_from: values.from,
							price_to: values.to,
							price_for_free: values.type === PRICE.FREE,
						},
					};
				}}
				onLocationInput={async (_event, suggestion, resolveGpsLocalityCallback) => {
					if (suggestion && suggestion.entityId === GET_GPS_LOCATION_ID) {
						const locality = await getCurrentPositionLocality(loadingGps);
						if (locality) {
							resolveGpsLocalityCallback(locality);
							watchdogData.value = {
								...watchdogData.value,
								filter: {
									...watchdogData.value.filter,
									locality: localityToFilterLocality(locality),
								},
							};
						}
					} else {
						watchdogData.value = {
							...watchdogData.value,
							filter: {
								...watchdogData.value.filter,
								locality: localitySuggestionToFilterLocality(
									suggestion ?? LOCALITY_CELA_CR_AS_SUGGESTION,
								),
							},
						};
					}
				}}
				onRadiusInput={(_event, radius) =>
					(watchdogData.value = {
						...watchdogData.value,
						filter: {
							...watchdogData.value.filter,
							radius_precision: radius,
						},
					})
				}
			/>
			<WatchdogForm
				onSubmit={onSubmit}
				forceDisabled={forceDisabled.value}
				isEdit={typeof editId.value === 'number'}
				values={{
					notificationInterval: notification_interval,
					useSeznamBar: use_seznam_bar,
				}}
				onUseSeznamBarChange={(value) =>
					(watchdogData.value = { ...watchdogData.value, use_seznam_bar: value })
				}
				onNotificationIntervalChange={(value) =>
					(watchdogData.value = { ...watchdogData.value, notification_interval: value })
				}
			/>
		</Modal>
	) : null;
}
